// src/pages/estoque/ModalEstoqueMov.js
import React, { useEffect, useMemo, useState } from 'react';
import { FaSortDown, FaSortUp } from 'react-icons/fa';
import { useMessage } from '../../contexts/geral/MessageContext';
import { getDescricaoTipoMov } from '../../models/estoque/TipoMov.ts';
import { buscarMovimentacoesEstoque } from '../../services/estoque/EstoqueService';
import { formatDate, formatTime } from '../../utils/dateTimeUtils';
import { formatNumeric } from '../../utils/numberUtils';
import Input from '../../components/input/Input';

const formatDateToInputValue = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');

    return `${year}-${month}-${day}`;
};

const ModalEstoqueMov = ({ show, onHide, produto }) => {
    const { showMessage } = useMessage();
    const [movimentacoes, setMovimentacoes] = useState([]);
    const [loading, setLoading] = useState(false);
    const [dataInicial, setDataInicial] = useState('');
    const [dataFinal, setDataFinal] = useState('');
    const [ordenacao, setOrdenacao] = useState({ campo: 'dataHoraMov', direcao: 'desc' });

    useEffect(() => {
        if (!show) return;

        const dataAtual = new Date();
        const primeiroDiaMes = new Date(dataAtual.getFullYear(), dataAtual.getMonth(), 1);
        setDataInicial(formatDateToInputValue(primeiroDiaMes));
        setDataFinal(formatDateToInputValue(dataAtual));
        setMovimentacoes([]);
    }, [show, produto?.produtoId]);

    const buscarMovimentacoes = async () => {
        if (!produto?.produtoId || !dataInicial || !dataFinal) return;

        try {
            setLoading(true);
            const result = await buscarMovimentacoesEstoque(
                produto.produtoId,
                dataInicial,
                dataFinal,
            );
            setMovimentacoes(Array.isArray(result) ? result : []);
        } catch (error) {
            showMessage(`Erro ao buscar movimentações: ${error?.message || error}`, 'danger');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (show && dataInicial && dataFinal) {
            buscarMovimentacoes();
        }
        // A consulta é refeita quando o produto ou o período forem alterados.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [show, produto?.produtoId, dataInicial, dataFinal]);

    const movimentacoesOrdenadas = useMemo(() => {
        const { campo, direcao } = ordenacao;
        const multiplicador = direcao === 'asc' ? 1 : -1;

        return [...movimentacoes].sort((a, b) => {
            const valorA = campo === 'tipoMovDescricao'
                ? getDescricaoTipoMov(a.tipoMov)
                : a?.[campo];
            const valorB = campo === 'tipoMovDescricao'
                ? getDescricaoTipoMov(b.tipoMov)
                : b?.[campo];

            if (['id', 'produtoId', 'qtdeMov', 'notaItemId'].includes(campo)) {
                return (Number(valorA || 0) - Number(valorB || 0)) * multiplicador;
            }

            if (campo === 'dataHoraMov') {
                return (new Date(valorA || 0) - new Date(valorB || 0)) * multiplicador;
            }

            return String(valorA || '').localeCompare(String(valorB || ''), 'pt-BR', {
                sensitivity: 'base',
            }) * multiplicador;
        });
    }, [movimentacoes, ordenacao]);

    const totais = useMemo(() => movimentacoes.reduce((acc, mov) => {
        const quantidade = Number(mov.qtdeMov || 0);

        if (Number(mov.tipoMov) === 1) acc.entradas += quantidade;
        if (Number(mov.tipoMov) === 2) acc.saidas += quantidade;

        return acc;
    }, { entradas: 0, saidas: 0 }), [movimentacoes]);

    const alterarOrdenacao = (campo) => {
        setOrdenacao((atual) => ({
            campo,
            direcao: atual.campo === campo && atual.direcao === 'asc' ? 'desc' : 'asc',
        }));
    };

    const renderOrdenadorColuna = (campo, titulo, style = {}) => {
        const ativa = ordenacao.campo === campo;

        return (
            <button
                type='button'
                onClick={() => alterarOrdenacao(campo)}
                style={{
                    background: 'none',
                    border: 'none',
                    padding: 0,
                    font: 'inherit',
                    fontWeight: 'bold',
                    color: 'inherit',
                    cursor: 'pointer',
                    width: '100%',
                    textAlign: style.textAlign || 'left',
                }}
            >
                {titulo}
                {ativa && (ordenacao.direcao === 'asc' ? <FaSortUp /> : <FaSortDown />)}
            </button>
        );
    };

    if (!show) return null;

    return (
        <div className='modal show d-block' tabIndex='-1'>
            <div className='modal-dialog modal-xl'>
                <div className='modal-content'>
                    <div className='modal-header'>
                        <h5 className='modal-title'>
                            Movimentação de Estoque - Produto {produto?.produtoId} ({produto?.produto?.descricao || '-'})
                        </h5>
                        <button type='button' className='btn-close' onClick={onHide} />
                    </div>
                    <div className='modal-body'>
                        <div className='group-div mb-3' style={{ alignItems: 'flex-end' }}>
                            <Input
                                label='Data Inicial'
                                type='date'
                                size={140}
                                value={dataInicial}
                                onChange={(e) => setDataInicial(e.target.value)}
                            />
                            <Input
                                label='Data Final'
                                type='date'
                                size={140}
                                value={dataFinal}
                                onChange={(e) => setDataFinal(e.target.value)}
                            />
                            <button
                                type='button'
                                className='btn btn-search'
                                onClick={buscarMovimentacoes}
                                disabled={loading}
                                style={{ marginBottom: '1px' }}
                            >
                                Buscar
                            </button>
                        </div>
                        <table className='table table-bordered table-sm'>
                            <thead>
                                <tr>
                                    <th style={{ textAlign: 'right' }}>
                                        {renderOrdenadorColuna('id', 'ID', { textAlign: 'right' })}
                                    </th>
                                    <th style={{ textAlign: 'right' }}>
                                        {renderOrdenadorColuna('produtoId', 'Produto ID', { textAlign: 'right' })}
                                    </th>
                                    <th>{renderOrdenadorColuna('tipoMovDescricao', 'Tipo Mov.')}</th>
                                    <th style={{ textAlign: 'right' }}>
                                        {renderOrdenadorColuna('qtdeMov', 'Qtde Mov.', { textAlign: 'right' })}
                                    </th>
                                    <th style={{ textAlign: 'center' }}>
                                        {renderOrdenadorColuna('dataHoraMov', 'Data', { textAlign: 'center' })}
                                    </th>
                                    <th style={{ textAlign: 'center' }}>
                                        {renderOrdenadorColuna('dataHoraMov', 'Hora', { textAlign: 'center' })}
                                    </th>
                                    <th style={{ textAlign: 'right' }}>
                                        {renderOrdenadorColuna('notaItemId', 'NotaItem ID', { textAlign: 'right' })}
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {loading && (
                                    <tr>
                                        <td colSpan={7}>Carregando movimentações...</td>
                                    </tr>
                                )}
                                {!loading && movimentacoesOrdenadas.map((mov) => (
                                    <tr key={mov.id}>
                                        <td style={{ textAlign: 'right' }}>{mov.id}</td>
                                        <td style={{ textAlign: 'right' }}>{mov.produtoId}</td>
                                        <td>{getDescricaoTipoMov(mov.tipoMov)}</td>
                                        <td style={{ textAlign: 'right' }}>{formatNumeric(mov.qtdeMov)}</td>
                                        <td style={{ textAlign: 'center' }}>{formatDate(mov.dataHoraMov)}</td>
                                        <td style={{ textAlign: 'center' }}>{formatTime(mov.dataHoraMov)}</td>
                                        <td style={{ textAlign: 'right' }}>{mov.notaItemId}</td>
                                    </tr>
                                ))}
                                {!loading && movimentacoes.length === 0 && (
                                    <tr>
                                        <td colSpan={7}>Nenhuma movimentação encontrada para este produto.</td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                    <div className='modal-footer justify-content-between'>
                        <div>
                            <strong>Total de Entradas: </strong> {formatNumeric(totais.entradas)}
                            {' | '}
                            <strong>Total de Saídas: </strong> {formatNumeric(totais.saidas)}
                        </div>
                        <button className='btn btn-secondary' onClick={onHide}>
                            Fechar
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ModalEstoqueMov;
